/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
import {useNavigate} from "react-router-dom"; // Import useNavigate hook for navigation between routes
import React, { useState} from "react"; // Import React and useState hook for state management
import CustomTh from "../../../../components/table/CustomTh"; // Import the CustomTh component for table headers
import CustomTd from "../../../../components/table/CustomTd"; // Import the CustomTd component for table cells
import DropDownMenuProfile from "../../../../components/DropDown/DropDownMenuProfile"; // Import drop-down menu component for actions
import DropDownMenuItem from "../../../../components/DropDown/DropDownMenuItem"; // Import drop-down menu item component
import TableFunction from "../../../../components/table/TableFunction"; // Import table function component for table logic
import {TABLES} from "../../../../Apis/Tables"; // Import table constants for different table options
import CustomChip from "../../../../components/CustomChip"; // Import CustomChip component for status chips
import {status} from "../../../../enums/statusEnum"; // Import status enums for contract statuses
import {statusBorderColor, statusColor, statusFontColor} from "../../../../enums/statusColorEnum"; // Import status color enums to display contract status with colors
import routes from "../../../../Routes/routes"; // Import routes for navigation
import {EMPTY_VALUES, getDateFormat} from "../../../../Apis/functions"; // Import date formatting function
import ContractDataApp from "./ContractDataApp"; // Import ContractDataApp component for contract-related data handling
import {useSelector} from "react-redux"; // Import useSelector hook to access Redux state
import {permissionObj} from "../../../../Helpers/permission"; // Import permission object for user access control
import {
    DetailsIconsOptions,
    TrashOutlineIcon,
    UpdateIconsOptions
} from "../../../../components/buttons/IconButtons";
import {VIEW_TYPE} from "../../../../enums/ViewType"; // Import visibility icon from Material UI

// Main component to display contract data in a table format
const ContractTableApp = (
    {
        searchElement,
        contractorId,
        selectedList,
        setSelectedList,
        modified,
        setModified,
        finalArray,
        setCriteria,
        setBodyForExport,
        setIsAllChecked,
        setDeleteContractShow
    }
) => {
    const DEV = useSelector((state) => state?.CommonsSlice?.development); // Get development mode state from Redux
    const { permission } = useSelector((state) => state.authenticatioauthennSlice); // Get user permissions from Redux state
    const navigate = useNavigate(); // Initialize navigate function for routing
    const [tableId, setTableId] = useState("tbl_cta_contract"); // Set ID for the table
    const [searchId, setSearchId] = useState("txt_cta_search_contract"); // Set ID for the search input
    const [results, setResults] = useState(null); // State to store the results data for the table
    const [option, setOption] = useState(`${TABLES.CONTRACTS}`); // Set the table option (contracts)
    const deletePermission = (DEV || permission?.includes(permissionObj?.WEB_CONTRACTOR_CONTRACT_DELETE));
    const leftPosition = deletePermission ? 50 : 0;

    // Function to generate the table headers (thead)
    const getThead = () => {
        let thead = [ <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes="first_head">{t('folio')}</CustomTh>];

        if(contractorId === null){
            thead.push(<CustomTh tableId={tableId}>{t('contractor')}</CustomTh>); // Display contractor if contractorId is null
        }

        thead.push(<CustomTh tableId={tableId}>{t('company')}</CustomTh>); // Display company
        thead.push(<CustomTh tableId={tableId}>{t('employees')}</CustomTh>); // Display number of employees
        thead.push(<CustomTh tableId={tableId}>{t('vehicles')}</CustomTh>); // Display number of vehicles
        thead.push(<CustomTh tableId={tableId}>{t('start_date')}</CustomTh>); // Display start date
        thead.push(<CustomTh tableId={tableId}>{t('end_date')}</CustomTh>); // Display end date
        thead.push(<CustomTh tableId={tableId}>{t('status')}</CustomTh>); // Display contract status
        return thead; // Return the generated table header rows
    };

    // Function to generate the table body (tbody)
    const getTbody = () => {
        // If no results, return empty body and data
        if (!results || results?.content?.length === 0) return {
            body: [], // Empty body
            data: []  // Empty data
        };

        // Map through the results to create a table row for each contract
        let body = results?.content?.map((item) => {
            return (
                <>
                    <CustomTd tableId={tableId} fixed={true} left={leftPosition} classes={"first-column"}>
                        {item?.folio || EMPTY_VALUES.DEFAULT} {/* Display folio */}
                    </CustomTd>
                    {contractorId === null && (
                        <CustomTd tableId={tableId}>
                            {`${item.contractorName || ''} ${item.contractorLastName || ''} ${item.contractorSecondLastName || ''}` || EMPTY_VALUES.DEFAULT}
                        </CustomTd>
                    )}
                    <CustomTd tableId={tableId}>
                        {`${item.contractorCompanyAcronym || ''} | ${item.contractorCompanyName || ''}` || EMPTY_VALUES.DEFAULT}
                    </CustomTd>

                    <CustomTd tableId={tableId}>{item?.noEmployees}</CustomTd> {/* Display number of employees */}
                    <CustomTd tableId={tableId}>{item?.noVehicles}</CustomTd> {/* Display number of vehicles */}
                    <CustomTd tableId={tableId}>{getDateFormat(item?.startDate, false)}</CustomTd> {/* Display formatted start date */}
                    <CustomTd tableId={tableId}>{getDateFormat(item?.endDate, false)}</CustomTd> {/* Display formatted end date */}
                    <CustomTd tableId={tableId}>
                        <CustomChip
                            legend={t(status[item?.statusId])}
                            borderColor={statusBorderColor[item?.statusId]}
                            fontColor={statusFontColor[item?.statusId]}
                            background={statusColor[item?.statusId]}
                            variant={'large'}/>
                    </CustomTd>
                </>
            );
        });

        return {
            body: body, // Return the generated body rows
            data: results?.content // Return the data associated with the rows
        };
    };

    // Function to generate action buttons for each row (e.g., view or edit buttons)
    const getActionsButtons = (item, index) => {
        return <>
            <DropDownMenuProfile
                openMenu={openMenu}
                handleMenuToggle={handleMenuToggle}
                menuId={`menu-${item.id}`} // Unique menu ID for each item
            >
                {(DEV || permission?.includes(permissionObj?.WEB_CONTRACTOR_CONTRACT_UPDATE)) && (
                    <DropDownMenuItem
                        id={"btn_cta_update_"+ item.id}
                        index={index}
                        handleClose={handleClose}
                        name={t('update')} // Label for update button
                        icon={<UpdateIconsOptions/>}
                        click={() => {
                            navigate(routes.employee.updateContract, {
                                state: { state: item.id }
                            }); // Navigate to contract update page
                        }}
                    />
                )}
                <DropDownMenuItem
                    id={"btn_cta_details_"+item.id}
                    index={index}
                    handleClose={handleClose}
                    name={t('details')} // Label for details button
                    icon={<DetailsIconsOptions/>}
                    click={() => {
                        navigate(contractorId === null
                            ? routes.employee.contractDetails + `${item.id}`
                            : routes.contractor.contractDetails + `${item.id}`); // Navigate to contract details page
                    }}
                />
                {(DEV || permission?.includes(permissionObj?.WEB_CONTRACTOR_CONTRACT_DELETE)) && (
                    <DropDownMenuItem
                        id={"btn_cta_delete_"+item.id}
                        index={index}
                        handleClose={handleClose}
                        name={t('delete')} // Translates 'delete'
                        fontColor='font-color-error-600'
                        click={() => {
                            setSelectedList([item.id]); // Update selected list state
                            setDeleteContractShow(true);
                        }}
                        icon={<TrashOutlineIcon className="icon-color-error-code-600" />} // Display delete icon
                    />
                )}
            </DropDownMenuProfile>
        </>;
    };

    // Handle contract data operations and pagination
    const {getData, getDataAdvancedSearch }  = ContractDataApp({
        searchElement,
        contractorId,
        setCriteria,
        setBodyForExport,
        setResults,
        option,
        finalArray,
        viewType: VIEW_TYPE.LIST.viewType
    });

    // Use the TableFunction component to handle table logic (checkbox selection, pagination, etc.)
    const { t, thead, tbody, handleCheckboxChange, handleDeleteAll,
        pagination, page, rowsPerPage,
        openMenu, handleMenuToggle, handleClose } = TableFunction({
        selectedList,
        setSelectedList, // Callback to update the selected rows
        data: results, // Table data
        getThead, // Function to generate the headers
        getTbody, // Function to generate the body
        labelRowsPerPage: 'contracts_per_page', // Label for rows per page
        hasContainer: true, // Enable container for the table
        fixedPagination: false, // Disable fixed pagination
        setIsAllChecked,

        searchElement,//New
        getData,//New
        modified,//New
        setModified,//New
        getDataAdvancedSearch,//New - apply only with advanced search
        finalArray,//New - apply only with advanced search
    });


    // Return the necessary props and methods to render the table
    return {
        t,
        DEV,
        navigate,
        permission,
        tableId,
        searchId,
        thead,
        tbody,
        pagination,
        getActionsButtons,
        handleCheckboxChange,
        handleDeleteAll,
        setResults,
        option,
        page,
        rowsPerPage,
        deletePermission
    };
}

export default ContractTableApp; // Export the component to be used in other parts of the app
